import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import type { AgentStatus } from '@/api/agentTypes';
import { apiErrorMessage } from '@/api/apiError';
import { useListAgentsQuery, useListHospitalsQuery } from '@/api/hubApi';

const STATUS_COLOR: Record<AgentStatus, 'success' | 'default' | 'error' | 'warning'> = {
    online: 'success',
    offline: 'default',
    pending: 'warning',
    error: 'error',
    revoked: 'default',
};

export function AgentsPage() {
    const { data: agents, isLoading, isFetching, error, refetch } = useListAgentsQuery();
    const { data: hospitals } = useListHospitalsQuery();

    const hospitalOf = (hospitalId: string) =>
        hospitals?.find((hospital) => hospital.id === hospitalId);

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Typography variant="h5">Hospitals agents</Typography>
                {isFetching && <CircularProgress size={20} />}
            </Box>

            {error && (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    action={
                        <Button color="inherit" size="small" onClick={() => refetch()}>
                            Retry
                        </Button>
                    }
                >
                    {apiErrorMessage(error, 'Failed to load agents')}
                </Alert>
            )}

            {isLoading ? (
                <CircularProgress />
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Hospital ID</TableCell>
                                <TableCell>Hospital name</TableCell>
                                <TableCell>Agent name</TableCell>
                                <TableCell>Agent ID</TableCell>
                                <TableCell>Region</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Scans</TableCell>
                                <TableCell>Focal point</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(agents ?? []).map((agent) => {
                                const hospital = hospitalOf(agent.hospital_id);
                                return (
                                    <TableRow key={agent.id} hover>
                                        <TableCell sx={{ fontFamily: 'monospace' }}>
                                            {agent.hospital_id}
                                        </TableCell>
                                        <TableCell>{hospital?.name ?? '—'}</TableCell>
                                        <TableCell>{agent.name}</TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace' }}>
                                            {agent.id}
                                        </TableCell>
                                        <TableCell>{hospital?.region ?? '—'}</TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={agent.status}
                                                color={STATUS_COLOR[agent.status]}
                                            />
                                        </TableCell>
                                        <TableCell align="right">{agent.scan_count}</TableCell>
                                        <TableCell>{hospital?.contact_email ?? '—'}</TableCell>
                                    </TableRow>
                                );
                            })}
                            {agents && agents.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        No agents registered yet.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}
